import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useRouter } from 'next/router';
import { wrapper } from 'store';
import DocumentHead from 'components/DocumentHead';
import slugify from 'slugify';
import uniqBy from 'lodash/uniqBy';

import { setProfile } from 'features/profilesSlice';
import axios from 'utils/axios';
import useProfileOwner from 'hooks/useProfileOwner';
import useScrollToTop from 'hooks/useScrollToTop';
import VerificationAlert from 'components/profileVerification/VerificationAlert';
import Profile from 'components/profile/Profile';

const ProfilePage = () => {
  const router = useRouter();
  const { username } = router.query;
  const profile = useSelector(state => state.profiles.mapping[username.toLowerCase()]);
  const dispatch = useDispatch();
  const profileOwner = useProfileOwner(profile);

  useScrollToTop();

  const inventory = profile?.inventories?.find(i => i.id === +router.query.i);
  const networkName = inventory?.influencer_network?.network?.name;
  const spaceName = inventory?.inventory_space?.name;
  const influencerNetworks = uniqBy(
    profile?.inventories.map(inventory => inventory.influencer_network),
    'id'
  );
  const filteredInfluencerNetworks = influencerNetworks.filter(
    influencerNetwork => influencerNetwork.network.automatic_verification
  );
  const verifiedAll = filteredInfluencerNetworks.every(influencerNetwork =>
    ['verified', 'pending_verification'].includes(influencerNetwork.status)
  );

  const getMetaUrl = () => {
    const profileUrl = `${process.env.NEXT_PUBLIC_URL}/u/${username}`;

    if (!inventory || !networkName || !spaceName) return profileUrl;

    return `${profileUrl}/${slugify(networkName, {
      lower: true,
    })}/${inventory.id}/${slugify(spaceName, { lower: true })}/${router.query.ii}`;
  };

  const getMetaTitle = () => {
    const defaultTitle = 'Check out my prices and buy from me directly on GigSocial';

    if (!inventory || !networkName || !spaceName) return defaultTitle;

    return `Buy ${networkName} ${spaceName} from me`;
  };

  const getMetaDescription = () => {
    if (!inventory || !networkName || !spaceName) return null;

    return `${profile.username} | GigSocial`;
  };

  const url = getMetaUrl();
  const title = getMetaTitle();
  const description = getMetaDescription();

  return (
    <>
      <DocumentHead>
        <title>{profile.display_name} | GigSocial</title>

        <meta name="robots" content="noindex" />
        <meta property="og:title" content={title} key="og:title" />
        <meta property="twitter:title" content={title} key="twitter:title" />

        {description && (
          <>
            <meta property="og:description" content={description} key="og:description" />
            <meta property="twitter:description" content={description} key="twitter:description" />
          </>
        )}

        {profile.avatar_url && !profile.adult && (
          <>
            <meta property="og:image" content={profile.avatar_url} key="og:image" />
            <meta name="twitter:image" content={profile.avatar_url} key="twitter:image" />
          </>
        )}

        {inventory?.assets?.[0]?.url && (
          <>
            <meta property="og:image" content={inventory.assets[0].url} key="og:image" />
            <meta name="twitter:image" content={inventory.assets[0].url} key="twitter:image" />
          </>
        )}

        <meta property="og:url" content={url} key="og:url" />
        <meta property="twitter:url" content={url} key="twitter:url" />
      </DocumentHead>

      {!verifiedAll && profileOwner && <VerificationAlert inventories={profile.inventories} />}

      <Profile profile={profile} setProfile={profile => dispatch(setProfile(profile))} />
    </>
  );
};

export const getServerSideProps = wrapper.getServerSideProps(store => async context => {
  const { params } = context;
  const { username } = params;

  try {
    const response = await axios.get(`/v1/profiles/${username}/public_profile`);
    store.dispatch(setProfile(response.data));
  } catch (error) {
    return {
      redirect: {
        destination: '/',
        permanent: false,
      },
    };
  }
});

export default ProfilePage;
